import { getSignupAttribution, loadSignupAttribution } from './signupAttribution';

const PROMOTEKIT_SIGNUP_STORAGE_PREFIX = 'instanttranscriber_promotekit_referred:';
const scheduledSignupRetries = new Set();
const isBrowser = () => typeof window !== 'undefined';
const cleanValue = value => value == null ? undefined : String(value).trim().slice(0, 500) || undefined;

export const capturePromoteKitAttribution = () => isBrowser()
  ? (window.instantTranscriberAcquisition?.affiliateFirstTouch?.() || { affiliate_state: 'none' })
  : { affiliate_state: 'none' };

export const initPromoteKitAttribution = () => {
  if (!isBrowser()) return;
  capturePromoteKitAttribution();
  [250, 1000, 2500, 5000].forEach(delay => window.setTimeout(capturePromoteKitAttribution, delay));
};

// Eligibility comes from the account's immutable server snapshot. Its original
// affiliate remains eligible after a delayed guest registration or payment;
// organic and legacy/unknown accounts never inherit a later browser cookie.
export const isNewPromoteKitSignup = user => {
  if (!user?.id || !user?.email || user.is_anonymous || !user.created_at) return false;
  if (Array.isArray(user.identities) && !user.identities.length) return false;
  const createdAt = Date.parse(user.created_at);
  const snapshot = getSignupAttribution(user);
  const registeredAt = Date.parse(snapshot?.registered_at);
  return Number.isFinite(createdAt) && createdAt <= Date.now()
    && snapshot?.user_id === user.id && snapshot.affiliate_state === 'affiliate'
    && Number.isFinite(registeredAt) && registeredAt <= Date.now()
    && Boolean(snapshot.promotekit_referral);
};

export const getPromoteKitCheckoutAttribution = user => {
  if (!isNewPromoteKitSignup(user)) return {};
  const snapshot = getSignupAttribution(user);
  return {
    promotekit_referral: snapshot.promotekit_referral,
    ...(snapshot.promotekit_via ? { promotekit_via: snapshot.promotekit_via } : {}),
  };
};

export const getPromoteKitRedirectAttributionParams = () => {
  const attribution = capturePromoteKitAttribution();
  const params = { affiliate_first_touch: attribution.affiliate_state };
  if (attribution.promotekit_referral) params.promotekit_referral = attribution.promotekit_referral;
  else if (attribution.promotekit_code_param && attribution.promotekit_code_value) {
    params[attribution.promotekit_code_param] = attribution.promotekit_code_value;
  }
  if (attribution.promotekit_via) params.via = attribution.promotekit_via;
  return params;
};

const hasTrackedSignup = (email) => {
  if (!isBrowser() || !email) return true;
  try {
    return Boolean(window.localStorage.getItem(`${PROMOTEKIT_SIGNUP_STORAGE_PREFIX}${email}`));
  } catch {
    return false;
  }
};

const markSignupTracked = (email) => {
  if (!isBrowser() || !email) return;
  try {
    window.localStorage.setItem(`${PROMOTEKIT_SIGNUP_STORAGE_PREFIX}${email}`, String(Date.now()));
  } catch {}
};

export const trackPromoteKitSignup = (user) => {
  if (!isBrowser()) return false;
  const original = getSignupAttribution(user);
  if (user?.email && !user.is_anonymous && original?.affiliate_state === 'affiliate'
      && original.registered_at && !original.promotekit_referral && original.promotekit_code_value) {
    const email = cleanValue(user.email)?.toLowerCase();
    if (email && !scheduledSignupRetries.has(email)) {
      scheduledSignupRetries.add(email);
      [300, 1000, 2500, 5000].forEach(delay => window.setTimeout(() => {
        loadSignupAttribution(user).then(() => trackPromoteKitSignup(user))
          .catch(error => console.warn('Affiliate resolution warning:', error.message));
      }, delay));
    }
    return false;
  }
  if (!isNewPromoteKitSignup(user)) return false;

  const normalizedEmail = cleanValue(user.email)?.toLowerCase();
  if (!normalizedEmail || hasTrackedSignup(normalizedEmail)) {
    return false;
  }

  const refer = window.promotekit?.refer;
  if (typeof refer !== 'function') {
    if (!scheduledSignupRetries.has(normalizedEmail)) {
      scheduledSignupRetries.add(normalizedEmail);
      [300, 1000, 2500, 5000].forEach((delay) => {
        window.setTimeout(() => trackPromoteKitSignup(user), delay);
      });
    }
    return false;
  }

  try {
    // PromoteKit's SDK reads this value synchronously before its request.
    // Pin it to the verified original referral even if a later link changed it.
    window.promotekit_referral = getSignupAttribution(user).promotekit_referral;
    const result = refer.call(window.promotekit, normalizedEmail);
    markSignupTracked(normalizedEmail);
    if (result && typeof result.catch === 'function') {
      result.catch((error) => {
        console.warn('PromoteKit signup tracking warning:', error?.message || error);
      });
    }
    return true;
  } catch (error) {
    console.warn('PromoteKit signup tracking warning:', error?.message || error);
    return false;
  }
};
